/**
 * Press and hold for what a control means (mobile redesign): the laptop explains buttons, column
 * heads and marks in hover tips (`title`), which a touch screen never shows. On a touch, holding
 * anything with a tip for half a second shows that tip in a bubble above the finger; lifting the
 * finger (or the next tap anywhere) puts it away, and the hold does not also press the control.
 *
 * What it reads, nearest first: `data-tip`, then `title`, then, for an icon-only control (no text
 * of its own), its `aria-label`. Places with a hold of their own opt out with `data-no-tip` (the
 * ranked rows, held to compare); the charts' canvases and text fields are left alone too. A drag
 * (the lap sheet, a scrolling list) moves the finger and so never shows a tip.
 *
 * Mounted once, for the whole app. Mice and pens keep the browser's own hover tips.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const HOLD_MS = 500;
const MOVE_PX = 10;
const SKIP = "[data-no-tip], canvas, input, textarea, select, [contenteditable='true']";

interface Tip {
  text: string;
  x: number;
  y: number;
}

/** The tip for an element under the finger, and the element it came from. */
export function tipFor(start: Element | null): { text: string; el: Element } | null {
  if (!start || start.closest(SKIP)) return null;
  for (let el: Element | null = start; el && el !== document.body; el = el.parentElement) {
    const own = el.getAttribute("data-tip") || el.getAttribute("title");
    if (own && own.trim()) return { text: own.trim(), el };
    // An icon-only button or link: its label is the only words it has.
    if (el.matches("button, a[href], [role=button], [role=tab]")) {
      const label = el.getAttribute("aria-label");
      if (label && label.trim() && !(el.textContent ?? "").trim()) return { text: label.trim(), el };
      // A control with words of its own: the words already say what it is.
      return null;
    }
  }
  return null;
}

export function LongPressTips() {
  const [tip, setTip] = useState<Tip | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let start: { x: number; y: number; id: number } | null = null;
    let shown = false;
    /** The click a hold ends in (until then) is swallowed without closing the tip. */
    let holdClickUntil = 0;

    const cancel = () => {
      clearTimeout(timer);
      start = null;
    };
    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== "touch" || !e.isPrimary) return;
      // A tap while a tip shows puts it away (and does nothing else).
      if (shown) return;
      cancel();
      const found = tipFor(e.target as Element);
      if (!found) return;
      start = { x: e.clientX, y: e.clientY, id: e.pointerId };
      timer = setTimeout(() => {
        shown = true;
        window.getSelection()?.removeAllRanges();
        navigator.vibrate?.(8);
        setTip({ text: found.text, x: start?.x ?? e.clientX, y: start?.y ?? e.clientY });
      }, HOLD_MS);
    };
    const onMove = (e: PointerEvent) => {
      if (start && e.pointerId === start.id && Math.hypot(e.clientX - start.x, e.clientY - start.y) > MOVE_PX) cancel();
    };
    const onUp = () => {
      clearTimeout(timer);
      if (shown && start) holdClickUntil = Date.now() + 700;
      start = null;
    };
    // After a hold, the browser's long-press menu and the click the hold ends in are swallowed;
    // the next tap anywhere closes the tip.
    const onContext = (e: Event) => {
      if (shown || start) e.preventDefault();
    };
    const onClick = (e: MouseEvent) => {
      if (!shown) return;
      e.preventDefault();
      e.stopPropagation();
      if (Date.now() < holdClickUntil) {
        holdClickUntil = 0;
        return;
      }
      shown = false;
      setTip(null);
    };
    const onScroll = () => {
      if (!shown) return;
      shown = false;
      setTip(null);
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("pointermove", onMove, true);
    document.addEventListener("pointerup", onUp, true);
    document.addEventListener("pointercancel", cancel, true);
    document.addEventListener("contextmenu", onContext, true);
    document.addEventListener("click", onClick, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("rt-tip-close", onScroll);
    return () => {
      window.removeEventListener("rt-tip-close", onScroll);
      clearTimeout(timer);
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("pointermove", onMove, true);
      document.removeEventListener("pointerup", onUp, true);
      document.removeEventListener("pointercancel", cancel, true);
      document.removeEventListener("contextmenu", onContext, true);
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, []);

  // A tip also goes by itself after a while.
  useEffect(() => {
    if (!tip) return;
    const t = setTimeout(() => window.dispatchEvent(new Event("rt-tip-close")), 6000);
    return () => clearTimeout(t);
  }, [tip]);

  if (!tip || typeof document === "undefined") return null;
  const width = Math.min(280, window.innerWidth - 24);
  const left = Math.max(12, Math.min(window.innerWidth - width - 12, tip.x - width / 2));
  // Above the finger, or below it near the top of the screen.
  const above = tip.y > 140;
  return createPortal(
    <div
      role="tooltip"
      data-long-press-tip
      className="pointer-events-none fixed z-[1000] whitespace-pre-line rounded-xl px-3.5 py-2.5 text-[14px] leading-snug shadow-2xl"
      style={{
        left,
        width,
        ...(above ? { bottom: window.innerHeight - tip.y + 44 } : { top: tip.y + 44 }),
        background: "rgba(18,18,42,0.97)",
        border: "1px solid #3a3a5c",
        color: "#e0e0e8",
      }}
    >
      {tip.text}
    </div>,
    document.body
  );
}
